import { aggregateRating } from '@iheartjane/menu';
import capitalize from 'lodash-es/capitalize';
import compact from 'lodash-es/compact';
import { JsonLd } from 'react-schemaorg';
import type { DayOfWeek, Store as SchemaStore, WithContext } from 'schema-dts';

/* eslint-disable-next-line no-restricted-imports */
import type { Store } from '@jane/shared/models';
import type { StoreSeo } from '@jane/shared/types';
import { monolithPaths } from '@jane/shared/util';

interface Props {
  store: Store;
  structuredData?: StoreSeo['structured_data'];
}

export const StoreDetailStructuredData = ({ store, structuredData }: Props) => {
  const {
    id,
    address,
    city,
    name,
    description,
    lat,
    long,
    medical,
    phone,
    photo,
    rating,
    recreational,
    reviews_count,
    state,
    working_hours,
    url_slug,
    zip,
  } = store;

  const hours = (working_hours ?? []).filter(
    (day) => day.period.from && day.period.to
  );
  const additionalProperties = compact([
    medical ? { name: 'Medical', value: medical } : undefined,
    recreational ? { name: 'Recreational', value: recreational } : undefined,
  ]);

  const storeSchema: WithContext<SchemaStore> = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    '@id': monolithPaths.store({ id, name }, undefined, url_slug),
    name,
    address: {
      '@type': 'PostalAddress',
      ...(city && { addressLocality: city }),
      ...(state && { addressRegion: state }),
      ...(zip && { postalCode: zip }),
      ...(address && { streetAddress: address }),
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: long,
    },
    ...(description ? { description } : {}),
    ...(phone ? { telephone: phone } : {}),
    ...(photo ? { image: photo } : {}),
    ...aggregateRating({
      aggregate_rating: rating,
      review_count: reviews_count,
    }),
    ...(hours.length > 0
      ? {
          openingHoursSpecification: hours
            .filter((day) => day.period.from && day.period.to)
            .map((day) => ({
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: capitalize(day.day) as DayOfWeek,
              ...(day.period.from && { opens: day.period.from }),
              ...(day.period.to && { closes: day.period.to }),
            })),
        }
      : {}),
    ...(additionalProperties.length > 0
      ? {
          additionalProperty: additionalProperties.map((property) => ({
            '@type': 'PropertyValue',
            name: property.name,
            value: property.value,
          })),
        }
      : {}),
    ...structuredData,
  };

  return <JsonLd item={storeSchema} />;
};
